import { Inject, Injectable } from '@angular/core';
import * as store from '@daas-consulting/core-frontend-store';
import {
  RemitosService,
  RemitosServiceId,
} from '@daas-consulting/production/remitos/frontend/data_contracts';
import * as models from '@daas-consulting/production/remitos/frontend/models';
import {
  errorMessage,
  loader,
} from '@daas-consulting/production/shared/frontend/store';
import { Action, State, StateContext, ofActionDispatched } from '@ngxs/store';
import { EMPTY, catchError, take, tap } from 'rxjs';
import * as actions from './remitos-detail_preview.actions';
import * as consts from './remitos-detail_preview.const';
import {
  Filter,
  RemitosDetailPreviewStateModel,
} from './remitos-detail_preview.state-model';

export const defaultFilter: Filter = {
  remitoId: null,
};

export const defaults: RemitosDetailPreviewStateModel = {
  requestFilterModel: defaultFilter,
  activeFilterModel: defaultFilter,
  requestFilterCriteria: [],
  activeFilterCriteria: [],
  requestItem: null,
  activeItem: null,
};

@State<RemitosDetailPreviewStateModel>({
  name: consts.stateTokenName,
  defaults,
})
@Injectable()
export class RemitosDetailPreviewState {
  readonly #service: RemitosService;
  readonly #eventHelper: store.EventHelper;

  constructor(
    @Inject(RemitosServiceId) service: RemitosService,
    @Inject(store.EventHelper) eventHelper: store.EventHelper
  ) {
    this.#service = service;
    this.#eventHelper = eventHelper;
    this._connectEvents();
  }

  @Action(actions.GetItem)
  getItem(
    ctx: StateContext<RemitosDetailPreviewStateModel>,
    { payload: remitoId }: actions.GetItem
  ): void {
    ctx.patchState({ requestItem: { remitoId } });
  }

  @Action(actions.GetItemRequest)
  getItemRequest(ctx: StateContext<RemitosDetailPreviewStateModel>): void {
    const state = ctx.getState();
    const remitoId = state?.requestItem?.remitoId;
    if (!remitoId) {
      ctx.dispatch(new loader.actions.HideLoading());
      return;
    }
    const conditionToCancelRequest = (x: typeof actions.GetItem): boolean => {
      const action = x as unknown as actions.GetItem;
      return action.payload !== remitoId;
    };
    this.#service
      .get$(remitoId)
      .pipe(
        take(1),
        this.#eventHelper.makeCancelOperator(
          actions.GetItem,
          conditionToCancelRequest,
          () => ctx.dispatch(new loader.actions.HideLoading())
        ),
        tap((item) =>
          ctx.dispatch(
            new actions.GetItemSuccessful(item as models.detailPreview.ItemView)
          )
        ),
        catchError((error) => {
          ctx.dispatch(new actions.GetItemFailure(error as Error));
          return EMPTY;
        })
      )
      .subscribe();
  }

  @Action(actions.GetItemSuccessful)
  getItemSuccessful(
    ctx: StateContext<RemitosDetailPreviewStateModel>,
    { payload }: actions.GetItemSuccessful
  ): void {
    ctx.patchState({ activeItem: payload });
  }

  @Action(actions.GetItemFailure)
  getItemFailure(
    ctx: StateContext<RemitosDetailPreviewStateModel>,
    { payload }: actions.GetItemFailure
  ) {
    return ctx.dispatch(new errorMessage.actions.ShowError(payload));
  }

  private _connectEvents(): void {
    this.#eventHelper.connectEvents(
      actions.GetItem,
      loader.actions.ShowLoading,
      ofActionDispatched
    );
    this.#eventHelper.connectEvents(actions.GetItem, actions.GetItemRequest);
    this.#eventHelper.connectEvents(
      actions.GetItemSuccessful,
      loader.actions.HideLoading
    );
    this.#eventHelper.connectEvents(
      actions.GetItemFailure,
      loader.actions.HideLoading
    );
  }
}
